"use client";

import FAQItem from "@/components/FAQItem";
import Magnetic from "@/components/ui/Magnetic";
import { FadeIn, FadeInStaggerItem, Stagger } from "@/components/ui/motion";
import { ArrowRight, Briefcase, Check, Loader2, Mail, MapPin, MessageSquare, Paperclip, Phone, UserPlus, XCircle } from "lucide-react";
import { useEffect, useState } from "react";

import { PixelTrailGrid } from "@/components/ui/PixelTrailGrid";
import TiltCard from "@/components/ui/TiltCard";
import dynamic from "next/dynamic";
const WorldMap = dynamic(() => import("@/components/WorldMap"), {
    ssr: false,
    loading: () => <div className="h-[400px] w-full animate-pulse bg-zinc-900/50 rounded-3xl" />
});

import { submitContactForm } from "@/app/actions/send-email";
import { trackGAEvent, trackGoogleAdsConversion, trackLinkedInConversion } from "@/lib/analytics";

export default function Contact() {
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [formLoadTime, setFormLoadTime] = useState<number>(0);
    const [purpose, setPurpose] = useState<string>("Project Partnership");
    const [resumeFile, setResumeFile] = useState<File | null>(null);
    const [resumeLinkVal, setResumeLinkVal] = useState("");

    useEffect(() => {
        setFormLoadTime(Date.now());
        if (typeof window !== "undefined") {
            const hash = window.location.hash;
            const searchParams = new URLSearchParams(window.location.search);
            if (hash === "#careers" || searchParams.get("purpose") === "careers") {
                setPurpose("Careers / Job Application");
            }
        }
    }, []);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const formElement = e.currentTarget;
        const formData = new FormData(formElement);

        const isAgent = (e as any).agentInvoked || (e.nativeEvent as any)?.agentInvoked;
        if (isAgent) {
            const promise = (async () => {
                const data = {
                    name: formData.get("name") as string,
                    email: formData.get("email") as string,
                    purpose: purpose,
                    resumeLink: purpose === "Careers / Job Application" ? (formData.get("resumeLink") as string || resumeLinkVal) : "",
                    linkedinProfile: purpose === "Careers / Job Application" ? (formData.get("linkedinProfile") as string) : "",
                    message: formData.get("message") as string,
                    honeypot: formData.get("hp_website") as string,
                    formLoadTime: formLoadTime || Date.now() - 5000,
                };
                return await submitContactForm(data);
            })();
            const respond = (e as any).respondWith || (e.nativeEvent as any)?.respondWith;
            if (typeof respond === "function") {
                respond.call(e.nativeEvent || e, promise);
            }
            return;
        }

        setLoading(true);

        let uploadedResumeUrl = "";
        if (purpose === "Careers / Job Application" && resumeFile) {
            try {
                const { ref, uploadBytes, getDownloadURL } = await import("firebase/storage");
                const { storage } = await import("@/lib/firebase");
                const storageRef = ref(storage, `resumes/${Date.now()}_${resumeFile.name}`);
                await uploadBytes(storageRef, resumeFile);
                uploadedResumeUrl = await getDownloadURL(storageRef);
            } catch (err) {
                console.error("Error uploading resume:", err);
                alert("Failed to upload resume file. Please try again.");
                setLoading(false);
                return;
            }
        }
        const data = {
            name: formData.get("name") as string,
            email: formData.get("email") as string,
            purpose: purpose,
            resumeLink: purpose === "Careers / Job Application" ? (uploadedResumeUrl || resumeLinkVal) : "",
            linkedinProfile: purpose === "Careers / Job Application" ? (formData.get("linkedinProfile") as string) : "",
            message: formData.get("message") as string,
            honeypot: formData.get("hp_website") as string,
            formLoadTime: formLoadTime,
        };

        const result = await submitContactForm(data);

        setLoading(false);
        if (result.success) {
            setSubmitted(true);
            import("@microsoft/clarity").then((clarityModule) => {
                clarityModule.default.event('lead_submit_contact_form');
            });
            
            // Track Google Analytics lead event
            trackGAEvent('generate_lead', {
                event_category: 'contact',
                event_label: 'contact_form_submission'
            });

            // Track Google Ads Conversion
            trackGoogleAdsConversion();

            // Track LinkedIn Insight Tag conversion
            trackLinkedInConversion();

            formElement.reset();
            setResumeFile(null);
            setResumeLinkVal("");
            // Reset the load time for subsequent submissions
            setFormLoadTime(Date.now());
            setTimeout(() => setSubmitted(false), 3000);
        } else {
            console.error(result.error);
            alert("Failed to send message. Please try again.");
        }
    };

    return (
        <div className="flex flex-col min-h-screen cursor-default bg-white font-sans text-black selection:bg-black selection:text-white">

            {/* Hero & Form Section */}
            <section className="relative pt-32 pb-20 overflow-hidden">
                <PixelTrailGrid className="absolute inset-0 z-0 h-full w-full pointer-events-none opacity-50" />

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <FadeIn>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
                            {/* Left Column: Context */}
                            <div>
                                <h1 className="text-5xl md:text-6xl font-bold tracking-tight mb-8 leading-tight">
                                    Let's build <br />
                                    <span className="text-gray-400">something iconic.</span>
                                </h1>
                                <p className="text-xl text-gray-600 leading-relaxed mb-12 max-w-lg">
                                    Ready to scale? Whether it's a new MVP, AI integration, or enterprise engineering, we're ready to deploy.
                                </p>

                                {/* Purpose selection as cards list */}
                                <div className="space-y-4 max-w-lg mb-12">
                                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-6">Select Purpose</label>
                                    {[
                                        {
                                            id: "Project Partnership",
                                            label: "Project Partnership",
                                            description: "Start a new MVP, integrate AI, or scale your product engineering.",
                                            icon: Briefcase,
                                        },
                                        {
                                            id: "Careers / Job Application",
                                            label: "Careers & Internships",
                                            description: "Apply for open engineering roles or send an open application.",
                                            icon: UserPlus,
                                        },
                                        {
                                            id: "General Inquiry",
                                            label: "General Inquiry",
                                            description: "Say hello, ask a general question, or explore other collaborations.",
                                            icon: MessageSquare,
                                        }
                                    ].map((opt) => {
                                        const Icon = opt.icon;
                                        const isSelected = purpose === opt.id;
                                        return (
                                            <button
                                                key={opt.id}
                                                type="button"
                                                disabled={loading}
                                                onClick={() => setPurpose(opt.id)}
                                                className={`w-full text-left flex items-start gap-4 p-5 rounded-2xl border transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${
                                                    isSelected
                                                        ? "bg-black border-black text-white shadow-xl shadow-gray-900/10 scale-[1.01]"
                                                        : "bg-white hover:bg-gray-50/80 border-gray-100 text-gray-900"
                                                }`}
                                            >
                                                <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${
                                                    isSelected ? "bg-white/10 text-white" : "bg-gray-50 text-gray-600 border border-gray-100"
                                                }`}>
                                                    <Icon className="h-5 w-5" />
                                                </div>
                                                <div>
                                                    <div className="font-bold text-sm">{opt.label}</div>
                                                    <div className={`text-xs mt-1 leading-relaxed ${
                                                        isSelected ? "text-gray-300" : "text-gray-500"
                                                    }`}>
                                                        {opt.description}
                                                    </div>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Right Column: Form */}
                            <div className="bg-white p-8 md:p-10 rounded-3xl border border-gray-100 shadow-2xl shadow-gray-200/50">
                                <form
                                    onSubmit={handleSubmit}
                                    className="space-y-8"
                                    toolname="submit-inquiry"
                                    tooldescription="Submits a client partnership inquiry, job application, or general inquiry directly to the DharmaTech team."
                                    toolautosubmit="true"
                                >
                                    {/* Honeypot field (hidden visually and from assistive tech) */}
                                    <div className="absolute opacity-0 -z-10 w-0 h-0 overflow-hidden pointer-events-none" aria-hidden="true">
                                        <input
                                            type="text"
                                            name="hp_website"
                                            tabIndex={-1}
                                            autoComplete="off"
                                            placeholder="Your website URL"
                                        />
                                    </div>

                                    {/* Hidden input to expose purpose choice dynamically to WebMCP agent compiler */}
                                    <input
                                        type="hidden"
                                        name="purpose"
                                        value={purpose}
                                        toolparamdescription="The primary purpose of your inquiry. Choose from 'Project Partnership', 'Careers / Job Application', or 'General Inquiry'."
                                    />

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="space-y-2">
                                            <label htmlFor="name" className="block text-sm font-bold text-gray-900">Name</label>
                                            <input
                                                type="text"
                                                id="name"
                                                name="name"
                                                required
                                                disabled={loading}
                                                className="w-full px-4 py-3 rounded-xl bg-gray-50 border-transparent focus:bg-white focus:border-black focus:ring-0 transition-all font-medium placeholder-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
                                                placeholder="John Doe"
                                                data-gramm="false"
                                                toolparamdescription="The inquirer's full name"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label htmlFor="email" className="block text-sm font-bold text-gray-900">Email</label>
                                            <input
                                                type="email"
                                                id="email"
                                                name="email"
                                                required
                                                disabled={loading}
                                                className="w-full px-4 py-3 rounded-xl bg-gray-50 border-transparent focus:bg-white focus:border-black focus:ring-0 transition-all font-medium placeholder-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
                                                placeholder="john@example.com"
                                                data-gramm="false"
                                                toolparamdescription="The inquirer's contact email address"
                                            />
                                        </div>
                                    </div>

                                    {purpose === "Careers / Job Application" && (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-in fade-in slide-in-from-top-4 duration-300">
                                            <div className="space-y-2">
                                                <label htmlFor="resumeLink" className="block text-sm font-bold text-gray-900">Resume Link / Attachment</label>
                                                <div className="relative flex items-center">
                                                    <input
                                                        type="text"
                                                        id="resumeLink"
                                                        name="resumeLink"
                                                        disabled={loading}
                                                        readOnly={!!resumeFile}
                                                        value={resumeLinkVal}
                                                        onChange={(e) => setResumeLinkVal(e.target.value)}
                                                        className="w-full pl-4 pr-12 py-3 rounded-xl bg-gray-50 border-transparent focus:bg-white focus:border-black focus:ring-0 transition-all font-medium placeholder-gray-400 disabled:opacity-75 disabled:cursor-not-allowed read-only:bg-gray-100 read-only:cursor-default text-gray-900"
                                                        placeholder={resumeFile ? "" : "Google Drive, Dropbox, PDF link..."}
                                                        data-gramm="false"
                                                        toolparamdescription="Link to your resume or portfolio (Only required for Careers / Job Application)."
                                                    />
                                                    <div className="absolute right-3 flex items-center">
                                                        {resumeFile ? (
                                                            <button
                                                                type="button"
                                                                disabled={loading}
                                                                onClick={() => {
                                                                    setResumeFile(null);
                                                                    setResumeLinkVal("");
                                                                }}
                                                                className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-gray-100 transition-colors cursor-pointer"
                                                                title="Remove attachment"
                                                                aria-label="Remove attachment"
                                                            >
                                                                <XCircle className="w-5 h-5" />
                                                            </button>
                                                        ) : (
                                                            <label className={`p-1.5 rounded-lg text-gray-400 hover:text-black hover:bg-gray-100 transition-colors cursor-pointer flex items-center justify-center ${loading ? 'pointer-events-none opacity-50' : ''}`}>
                                                                <Paperclip className="w-5 h-5" />
                                                                <input 
                                                                    type="file" 
                                                                    className="hidden" 
                                                                    accept=".pdf,image/png,image/jpeg,image/jpg"
                                                                    disabled={loading}
                                                                    onChange={(e) => {
                                                                        const file = e.target.files?.[0];
                                                                        if (file) {
                                                                            if (file.size > 3 * 1024 * 1024) {
                                                                                alert("File size exceeds 3MB limit.");
                                                                                return;
                                                                            }
                                                                            setResumeFile(file);
                                                                            setResumeLinkVal(file.name);
                                                                        }
                                                                    }}
                                                                />
                                                            </label>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="space-y-2">
                                                <label htmlFor="linkedinProfile" className="block text-sm font-bold text-gray-900">LinkedIn / Portfolio URL</label>
                                                <input
                                                    type="url"
                                                    id="linkedinProfile"
                                                    name="linkedinProfile"
                                                    disabled={loading}
                                                    className="w-full px-4 py-3 rounded-xl bg-gray-50 border-transparent focus:bg-white focus:border-black focus:ring-0 transition-all font-medium placeholder-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    placeholder="linkedin.com/in/username"
                                                    data-gramm="false"
                                                    toolparamdescription="LinkedIn profile or personal portfolio URL (Optional, but recommended for Careers / Job Application)."
                                                />
                                            </div>
                                        </div>
                                    )}

                                    <div className="space-y-2">
                                        <label htmlFor="message" className="block text-sm font-bold text-gray-900">
                                            {purpose === "Careers / Job Application" ? "Cover Letter / About You" : "Project Details"}
                                        </label>
                                        <textarea
                                            id="message"
                                            name="message"
                                            rows={4}
                                            required
                                            disabled={loading}
                                            className="w-full px-4 py-3 rounded-xl bg-gray-50 border-transparent focus:bg-white focus:border-black focus:ring-0 transition-all font-medium placeholder-gray-400 resize-none disabled:opacity-50 disabled:cursor-not-allowed"
                                            placeholder={
                                                purpose === "Careers / Job Application"
                                                    ? "Introduce yourself, share what roles you are interested in, or paste key highlights from your resume..."
                                                    : "Tell us about your goals, timeline, and budget..."
                                            }
                                            data-gramm="false"
                                            toolparamdescription="Details about the project partnership, goals, timeline, budget, or cover letter information for careers."
                                        ></textarea>
                                    </div>

                                    <div className="pt-2">
                                        <Magnetic>
                                            <button
                                                type="submit"
                                                disabled={loading || submitted}
                                                className="w-full group relative flex items-center justify-center space-x-2 bg-black text-white px-8 py-4 rounded-xl font-bold hover:bg-gray-800 transition-all disabled:opacity-70 disabled:cursor-not-allowed shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                                            >
                                                <span>{loading ? "Sending..." : submitted ? "Message Sent" : "Send Message"}</span>
                                                {!loading && !submitted && <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />}
                                                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                                                {submitted && <Check className="h-4 w-4" />}
                                            </button>
                                        </Magnetic>
                                    </div>

                                    <p className="text-center text-xs text-gray-400 mt-4">
                                        Usually responds in 24 hours. No spam, ever.
                                    </p>
                                </form>
                            </div>
                        </div>
                    </FadeIn>
                </div>
            </section>

            {/* Global Offices using TiltCard */}
            <section className="py-20 bg-gray-50/50 border-y border-gray-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <FadeIn>
                        <div className="mb-12">
                            <h2 className="text-3xl font-bold mb-4">Global HQ</h2>
                            <p className="text-gray-500 max-w-2xl">Our distributed team operates across three major time zones, ensuring continuous development cycles.</p>
                        </div>
                    </FadeIn>

                    <Stagger className="flex justify-center">
                        <FadeInStaggerItem>
                            <TiltCard className="bg-white p-8 border border-gray-200 shadow-sm h-full flex flex-col group">
                                <div className="h-10 w-10 bg-black text-white flex items-center justify-center rounded-lg mb-6">
                                    <span className="font-bold text-xs">IN</span>
                                </div>
                                <h3 className="text-xl font-bold mb-2">India (HQ)</h3>
                                <div className="text-gray-500 text-sm mb-6 flex-1">
                                    <p>3rd Floor, Pran Prabha, Nanpura, Surat, IN - 395001</p>
                                    <a
                                        href="https://share.google/1xDmAT6jVxED5RDZs"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-xs font-bold uppercase tracking-widest text-black mt-2 inline-flex items-center gap-1 hover:underline"
                                    >
                                        <MapPin className="h-3 w-3" /> View on Map
                                    </a>
                                </div>

                                <div className="space-y-3 pt-6 border-t border-gray-100">
                                    <a href="mailto:contact@dharmatech.in" className="flex items-center text-sm font-medium hover:text-black text-gray-500 transition-colors gap-2">
                                        <Mail className="h-4 w-4" /> contact@dharmatech.in
                                    </a>
                                    <a href="tel:+916353689199" className="flex items-center text-sm font-medium hover:text-black text-gray-500 transition-colors gap-2">
                                        <Phone className="h-4 w-4" /> +91 63536 89199
                                    </a>
                                </div>
                            </TiltCard>
                        </FadeInStaggerItem>

                        {/* US and Indonesia Office Cards Removed */}

                    </Stagger>
                </div>
            </section>

            {/* World Map Section - Dark Divider */}
            <div className="w-full bg-[#050505] py-24 relative overflow-hidden">
                <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:40px_40px]"></div>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <FadeIn>
                        <div className="text-center mb-16">
                            <h2 className="text-3xl md:text-5xl font-bold mb-6 text-white tracking-tight">Global Impact</h2>
                            <p className="text-gray-400 max-w-2xl mx-auto text-lg">
                                Delivering excellence to clients worldwide.
                            </p>
                        </div>
                        <div className="bg-white/5 p-2 rounded-[2.5rem] border border-white/10 shadow-2xl backdrop-blur-sm">
                            <WorldMap theme="dark" />
                        </div>
                    </FadeIn>
                </div>
            </div>

            {/* FAQ */}
            <section className="py-20 bg-white border-t border-zinc-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
                        {/* Left side: Sticky Title & Description */}
                        <div className="lg:col-span-4">
                            <FadeIn>
                                <div className="lg:sticky lg:top-28">
                                    <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-zinc-900">
                                        Common <br className="hidden lg:block" /> Questions
                                    </h2>
                                    <p className="mt-4 text-sm text-zinc-500 max-w-sm leading-relaxed">
                                        Quick answers to common questions about our communication and availability.
                                    </p>
                                </div>
                            </FadeIn>
                        </div>

                        {/* Right side: Accordion list */}
                        <div className="lg:col-span-8">
                            <Stagger className="space-y-0">
                                <FadeInStaggerItem>
                                    <FAQItem
                                        question="What are your office hours?"
                                        answer="We work Mon-Fri, 10:00 AM to 7:00 PM IST. However, our support team is available 24/7 for critical issues."
                                    />
                                </FadeInStaggerItem>
                                <FadeInStaggerItem>
                                    <FAQItem
                                        question="How fast do you respond to inquiries?"
                                        answer="We typically respond to all new project inquiries within 24 hours."
                                    />
                                </FadeInStaggerItem>
                            </Stagger>
                        </div>
                    </div>
                </div>
            </section>

            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                        '@context': 'https://schema.org',
                        '@type': 'FAQPage',
                        mainEntity: [
                            {
                                '@type': 'Question',
                                name: 'What are your office hours?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'We work Mon-Fri, 10:00 AM to 7:00 PM IST. However, our support team is available 24/7 for critical issues.'
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'How fast do you respond to inquiries?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'We typically respond to all new project inquiries within 24 hours.'
                                }
                            }
                        ]
                    })
                }}
            />
        </div>
    );
}